import { ESdChangeReason } from "@kuika/kuika-cl-model";
import Convert from "ansi-to-html";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { handleFixAllWithAI, navigateToGroupSource } from "../../../../../shared/utility/issues/issues-actions";
import {
  ErrorSourceCategory,
  getErrorCounts,
  getFixableValidationCount,
  getGroupedErrors,
  getIssueIdentityKey,
  IErrorCounts,
  IErrorGroup,
  IGroupedErrorItem
} from "../../../../../shared/utility/issues/issues-model";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { IssueRow } from "./issue-row";
import "./issues.scss";

type IssuesTab = "all" | "error" | "warning";

/** The tabs in their on-screen order, so a list slides in from the side its tab lies on. */
const ISSUES_TAB_ORDER: IssuesTab[] = ["all", "error", "warning"];

const toIssuesTab = (value: string): IssuesTab =>
  ISSUES_TAB_ORDER.find((tab: IssuesTab): boolean => tab === value) ?? "all";

// Severity tint a tab's count chip takes while that tab is selected (unselected
// chips are always neutral grey — see .kuika-issues__tab-count in issues.scss).
type TabCountVariant = "error" | "warning" | "neutral";

// Category → plural section label + display order for the popover body.
const CATEGORY_ORDER: ErrorSourceCategory[] = [
  "screen",
  "workflow",
  "scheduledJob",
  "apiManagement",
  "bluetooth",
  "csharp",
  "dataType",
  "datasource",
  "codeGeneration"
];

const CATEGORY_SECTION_LABEL: Record<ErrorSourceCategory, string> = {
  screen: "Screens",
  workflow: "Processes",
  scheduledJob: "Scheduled Jobs",
  apiManagement: "APIs",
  bluetooth: "BT Datasources",
  csharp: "C# Datasources",
  dataType: "Data Types",
  datasource: "SQL",
  codeGeneration: "Code Generation"
};

// Sources the panel cannot navigate to — their "GO TO SOURCE" affordance is hidden.
const NON_NAVIGABLE_CATEGORIES: ErrorSourceCategory[] = ["dataType", "datasource", "codeGeneration"];

export interface IIssuesPanelProps {
  onNavigate?: () => void;
  // Whether the popover is expanded to the large in-place panel.
  expanded?: boolean;
  onSetExpanded?: (expanded: boolean) => void;
}

// escapeXML:true — the converted text is a codeGenException (user-influenced) rendered via
// dangerouslySetInnerHTML, so HTML in it must be escaped, not passed through.
const convert = new Convert({ newline: true, fg: "#333", escapeXML: true });

export interface IIssuesPanelState {
  activeTab: IssuesTab;
  collapsedKeys: Set<string>;
}

class IssuesPanel extends PureSdComponent<IIssuesPanelProps & IWithSDProps, IIssuesPanelState> {
  constructor(props: IIssuesPanelProps & IWithSDProps) {
    super(props);
    this.state = { activeTab: "all", collapsedKeys: new Set<string>() };
  }

  // Filter a group's items to a tab (Error / Warnings). "All" keeps everything. The tab is passed
  // in, not read from state, because a leaving tab's list is drawn while the tabs swap.
  private itemsForTab = (group: IErrorGroup, tab: IssuesTab): IGroupedErrorItem[] => {
    if (tab === "all") return group.items;
    return group.items.filter((item: IGroupedErrorItem) => {
      if (item.type !== "validation") {
        // Datasource + code-generation items are always errors.
        return tab === "error";
      }
      const isWarning: boolean = item.validation?.type === "Warning";
      return tab === "warning" ? isWarning : !isWarning;
    });
  };

  private toggleGroup = (sourceKey: string): void => {
    this.setState((prev: IIssuesPanelState) => {
      const next = new Set<string>(prev.collapsedKeys);
      if (next.has(sourceKey)) {
        next.delete(sourceKey);
      } else {
        next.add(sourceKey);
      }
      return { collapsedKeys: next };
    });
  };

  private handleGoToSource = (group: IErrorGroup): void => {
    navigateToGroupSource(this.props.sd, group);
    this.props.onNavigate?.();
  };

  // Round count pill — same language as the toolbar trigger badge.
  private renderCount = (count: number, variant: "error" | "warning", testId: string): JSX.Element => (
    <span className={`kuika-issues__count kuika-issues__count--${variant}`} data-testid={testId}>
      {count}
    </span>
  );

  // Tab label — the text plus its own count chip (per Figma), instead of baking the
  // number into the label string. The chip stays grey until its tab is selected.
  private renderTabLabel = (text: string, count: number, variant: TabCountVariant): JSX.Element => (
    <span className="kuika-issues__tab">
      <span className="kuika-issues__tab-text">{text}</span>
      <span className={`kuika-issues__tab-count kuika-issues__tab-count--${variant}`}>{count}</span>
    </span>
  );

  // "All" is an aggregate, so it borrows the tint of the worst severity present.
  private allTabVariant = (counts: IErrorCounts): TabCountVariant => {
    if (counts.errorCount > 0) return "error";
    if (counts.warningCount > 0) return "warning";
    return "neutral";
  };

  // Header count — a wider soft pill holding a coloured dot + the number (per Figma).
  private renderHeaderCount = (count: number, variant: "error" | "warning"): JSX.Element => (
    <span className={`kuika-issues__hcount kuika-issues__hcount--${variant}`}>
      <span className="kuika-issues__hdot" />
      {count}
    </span>
  );

  // Static, non-expandable rows. Validation rows are their own component (IssueRow) so a
  // detail toggle re-renders one row instead of the whole panel.
  private renderItemRow = (item: IGroupedErrorItem, key: string): JSX.Element | null => {
    if (item.type === "datasource") {
      return (
        <div
          className="kuika-issues__item kuika-issues__item--static kuika-issues__item--error"
          data-testid="issuesRow"
          key={key}
        >
          <KuikaSvg
            name="circle-x"
            type="common"
            width={18}
            height={18}
            className="kuika-issues__item-icon kuika-issues__item-icon--error"
          />
          <span className="kuika-issues__item-message" data-testid="issuesRowMessage">
            {item.datasourceMessage}
          </span>
        </div>
      );
    }

    if (item.type === "codeGeneration") {
      // When expanded, show the full exception inline; otherwise a compact row with a
      // "View details" affordance that expands the popover in place.
      if (this.props.expanded) {
        return (
          <div className="kuika-issues__codegen" key={key}>
            <div
              className="kuika-issues__item kuika-issues__item--static kuika-issues__item--error"
              data-testid="issuesCodeGenFailedRow"
            >
              <KuikaSvg
                name="circle-x"
                type="common"
                width={18}
                height={18}
                className="kuika-issues__item-icon kuika-issues__item-icon--error"
              />
              <span className="kuika-issues__item-message" data-testid="issuesRowMessage">
                Code generation failed
              </span>
            </div>
            <pre
              className="kuika-issues__codegen-output"
              data-testid="issuesCodeGenOutput"
              dangerouslySetInnerHTML={{ __html: convert.toHtml(item.codeGenException ?? "") }}
            />
          </div>
        );
      }
      return (
        <div
          className="kuika-issues__item kuika-issues__item--error"
          data-testid="issuesCodeGenFailedRow"
          key={key}
          onClick={() => this.props.onSetExpanded?.(true)}
        >
          <KuikaSvg
            name="circle-x"
            type="common"
            width={18}
            height={18}
            className="kuika-issues__item-icon kuika-issues__item-icon--error"
          />
          <span className="kuika-issues__item-message" data-testid="issuesRowMessage">
            Code generation failed
          </span>
          <span className="kuika-issues__codegen-view">View details</span>
        </div>
      );
    }

    return null;
  };

  /**
   * A group's rows. A validation row is keyed by the ISSUE'S IDENTITY, not its position:
   * `IssueRow` keeps its own expanded flag, and with a positional key React would hand that
   * instance — still expanded — to whatever validation the severity filter moved into the
   * slot. Identical validations inside one group would collide on that key, so repeats get a
   * deterministic `#n` suffix in list order rather than a React duplicate-key warning.
   */
  private renderGroupItems = (group: IErrorGroup, items: IGroupedErrorItem[]): (JSX.Element | null)[] => {
    const seen: Map<string, number> = new Map<string, number>();
    return items.map((item: IGroupedErrorItem, idx: number) => {
      if (!item.validation) return this.renderItemRow(item, `${group.sourceKey}-${idx}`);
      const identity: string = getIssueIdentityKey(group.sourceKey, item.validation);
      const repeat: number = seen.get(identity) ?? 0;
      seen.set(identity, repeat + 1);
      const key: string = repeat === 0 ? identity : `${identity}#${repeat}`;
      return <IssueRow key={key} validation={item.validation} sd={this.props.sd} onNavigate={this.props.onNavigate} />;
    });
  };

  private renderGroup = (group: IErrorGroup, tab: IssuesTab): JSX.Element | null => {
    const items: IGroupedErrorItem[] = this.itemsForTab(group, tab);
    if (items.length === 0) return null;
    const isCollapsed: boolean = this.state.collapsedKeys.has(group.sourceKey);
    const hasNavigation: boolean = !NON_NAVIGABLE_CATEGORIES.includes(group.category);

    return (
      <div className="kuika-issues__group" key={group.sourceKey}>
        <div className="kuika-issues__group-header">
          <button
            className={`kuika-issues__group-toggle ${OVERFLOW_REVEAL_HOST_CLASS}`}
            onClick={() => this.toggleGroup(group.sourceKey)}
          >
            <KuikaSvg
              name="chevron-right"
              type="arrows"
              width={14}
              height={14}
              className={`kuika-issues__chevron ${isCollapsed ? "" : "kuika-issues__chevron--open"}`}
            />
            <span className="kuika-issues__group-name" data-testid={`issuesGroupName-${group.displayName}`}>
              <KuikaOverflowText>{group.displayName}</KuikaOverflowText>
            </span>
            {tab !== "warning" &&
              group.errorCount > 0 &&
              this.renderCount(group.errorCount, "error", `issuesGroupErrorCount-${group.displayName}`)}
            {tab !== "error" &&
              group.warningCount > 0 &&
              this.renderCount(group.warningCount, "warning", `issuesGroupWarningCount-${group.displayName}`)}
          </button>
          {hasNavigation && (
            <span
              className="kuika-issues__go-to-source"
              data-testid={`issuesGroupGoToSource-${group.displayName}`}
              onClick={() => this.handleGoToSource(group)}
            >
              GO TO SOURCE
            </span>
          )}
        </div>
        {!isCollapsed && <div className="kuika-issues__group-items">{this.renderGroupItems(group, items)}</div>}
      </div>
    );
  };

  render() {
    const { activeTab } = this.state;
    const counts: IErrorCounts = getErrorCounts(this.props.sd);
    const totalCount: number = counts.errorCount + counts.warningCount;
    const groups: IErrorGroup[] = getGroupedErrors(this.props.sd);
    const fixableCount: number = getFixableValidationCount(this.props.sd);

    // Group the flat source-groups by category so the body reads as sectioned
    // ("Screens", "Processes", …), matching the design.
    const groupsByCategory: Map<ErrorSourceCategory, IErrorGroup[]> = new Map<ErrorSourceCategory, IErrorGroup[]>();
    for (const group of groups) {
      const list = groupsByCategory.get(group.category) || [];
      list.push(group);
      groupsByCategory.set(group.category, list);
    }

    // One tab's list. Drawn from its own tab so that, while the tabs swap, the leaving pane is still
    // the list it was.
    const renderTabList = (tab: IssuesTab): React.ReactNode => {
      const hasVisibleGroups: boolean = groups.some((g: IErrorGroup) => this.itemsForTab(g, tab).length > 0);
      if (!hasVisibleGroups) {
        return (
          <div className="kuika-issues__empty">
            <KuikaBadge color="green" content="No issues" />
          </div>
        );
      }
      return CATEGORY_ORDER.map((category: ErrorSourceCategory) => {
        const catGroups: IErrorGroup[] | undefined = groupsByCategory.get(category);
        if (!catGroups || catGroups.length === 0) return null;
        const renderedGroups: (JSX.Element | null)[] = catGroups.map((g: IErrorGroup) => this.renderGroup(g, tab));
        if (renderedGroups.every((el) => el === null)) return null;
        return (
          <div className="kuika-issues__section" key={category}>
            <div className="kuika-issues__section-label">{CATEGORY_SECTION_LABEL[category]}</div>
            {renderedGroups}
          </div>
        );
      });
    };

    return (
      <div className="kuika-issues" data-testid="issuesPanel">
        <div className="kuika-issues__header">
          <div className="kuika-issues__title">
            <KuikaSvg name="info-circle" width={16} height={16} />
            <span>Issues</span>
            {counts.errorCount > 0 && this.renderHeaderCount(counts.errorCount, "error")}
            {counts.warningCount > 0 && this.renderHeaderCount(counts.warningCount, "warning")}
          </div>
          <div className="kuika-issues__header-actions">
            {fixableCount > 0 && (
              <KuikaTooltip content="Fix all AI-fixable validations in a new chat" position="top">
                <button
                  className="kuika-issues__fix-btn kuika-issues__fix-btn--all"
                  onClick={() => {
                    handleFixAllWithAI(this.props.sd);
                    this.props.onNavigate?.();
                  }}
                >
                  <KuikaSvg name="wand" type="common" width={12} height={12} />
                  <span>Fix all with AI ({fixableCount})</span>
                </button>
              </KuikaTooltip>
            )}
            <KuikaTooltip content={this.props.expanded ? "Collapse" : "Expand"} position="top">
              <button
                className="kuika-issues__expand-btn"
                onClick={() => this.props.onSetExpanded?.(!this.props.expanded)}
                aria-label={this.props.expanded ? "Collapse" : "Expand"}
              >
                <KuikaSvg name={this.props.expanded ? "minimize" : "maximize"} width={16} height={16} />
              </button>
            </KuikaTooltip>
          </div>
        </div>

        <div className="kuika-issues__tabs">
          <KuikaTabs
            size="sm"
            value={activeTab}
            onChange={(value: string) => this.setState({ activeTab: toIssuesTab(value) })}
            options={[
              { value: "all", label: this.renderTabLabel("All", totalCount, this.allTabVariant(counts)) },
              { value: "error", label: this.renderTabLabel("Error", counts.errorCount, "error") },
              { value: "warning", label: this.renderTabLabel("Warnings", counts.warningCount, "warning") }
            ]}
          />
        </div>

        <div className="kuika-issues__scroll">
          <KuikaTabContent
            activeKey={activeTab}
            order={ISSUES_TAB_ORDER}
            render={(tab: string): React.ReactNode => renderTabList(toIssuesTab(tab))}
          />
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.DesignTimeElementStateChanged) return true;
    return false;
  };
}

const issuesPanel = withSD<IIssuesPanelProps>(IssuesPanel);
export { issuesPanel as IssuesPanel };
